import React from 'react';
import styled, { css } from 'styled-components';
import { Tooltip as BaseTooltip } from './Tooltip';
import { Radio as BaseRadio } from './Radio';
var Orientation;
(function (Orientation) {
    Orientation["Horizontal"] = "horizontal";
    Orientation["Vertical"] = "vertical";
})(Orientation || (Orientation = {}));
const Description = styled.div `
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  font-size: 12px;
  line-height: 1.5;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
`;
const Label = styled.label `
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  color: ${({ theme }) => theme.secondary.background};
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  transition: color 0.2s;
`;
const Radio = (props) => {
    return React.createElement(BaseRadio, { ...props, isChecked: props.isActive, onCheckedChange: props.onClick });
};
const Tooltip = styled(BaseTooltip) `
  position: absolute;
  top: 16px;
  right: 16px;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  transition: color 0.2s;

  svg {
    --icon-size: 16px;
  }

  &:hover {
    color: ${({ theme }) => theme.secondary.background};
  }
`;
const OptionBase = styled.div `
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  line-height: 1;
  position: relative;
  padding: 16px 16px 16px 40px;
  color: ${({ theme }) => theme.secondary.background};
  opacity: ${({ isDisabled }) => (isDisabled ? '.8' : '1')};
  display: flex;
  gap: 4px;
  flex-direction: column;

  &:hover {
    ${Label} {
      color: ${({ theme }) => theme.foreground};
    }
  }

  background-color: ${({ isMuted, theme }) => (isMuted ? theme.secondary.blendToBackground(50) : 'transparent')};

  > *:first-child {
    position: absolute;
    top: 16px;
    left: 16px;
  }

  ${({ isDisabled }) => isDisabled &&
    css `
      cursor: not-allowed;
    `}

  ${({ isActive, theme }) => isActive &&
    `
    ${Label} {
      color: ${theme.foreground};
    }
  `}
`;
const GroupWrapper = styled.div `
  background-color: ${({ theme }) => theme.background};
  border-radius: ${({ theme }) => theme.style.borderRadiusM};
  box-shadow:
    ${({ theme }) => theme.style.buttonSecondaryBorderProperties}
      ${({ theme }) => theme.style.buttonSecondaryBorderWidth} ${({ theme }) => theme.style.buttonSecondaryBorderColor},
    0 1px 1px rgba(0, 0, 0, 0.1),
    0 1px 4px 0 transparent inset;
  display: grid;

  ${({ noBorder }) => noBorder &&
    css `
      box-shadow: none;
    `}

  ${OptionBase} {
    border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};
  }

  ${({ orientation }) => orientation === 'horizontal' &&
    css `
      grid-auto-flow: column;
      grid-auto-columns: 1fr;

      ${OptionBase} {
        border-bottom: none;
        border-radius: 0;
        border-right: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};

        &:first-child {
          border-top-left-radius: ${({ theme }) => theme.style.borderRadiusM};
          border-bottom-left-radius: ${({ theme }) => theme.style.borderRadiusM};
        }

        &:last-child {
          border-top-right-radius: ${({ theme }) => theme.style.borderRadiusM};
          border-bottom-right-radius: ${({ theme }) => theme.style.borderRadiusM};
          border-right: none;
        }
      }
    `}

  ${({ orientation }) => orientation === 'vertical' &&
    css `
      grid-auto-flow: row;

      & > div {
        border-radius: 0;

        &:first-child {
          border-top-left-radius: ${({ theme }) => theme.style.borderRadiusM};
          border-top-right-radius: ${({ theme }) => theme.style.borderRadiusM};
        }

        &:last-child {
          border-bottom-left-radius: ${({ theme }) => theme.style.borderRadiusM};
          border-bottom-right-radius: ${({ theme }) => theme.style.borderRadiusM};
          border-bottom: none;
        }
      }
    `}

  ${({ theme }) => theme.id !== 'default' &&
    css `
      background-color: ${theme.style.dropdownBackgroundColor};
    `}
`;
const OptionComponents = { Description, Label, Tooltip, Radio };
const Option = Object.assign(({ children, ...props }) => (React.createElement(OptionBase, { ...props }, React.Children.map(children, (child) => React.isValidElement(child) ? React.cloneElement(child, props) : child))), OptionComponents);
const OptionGroupComponents = { Option, Orientation };
const OptionGroup = Object.assign(({ orientation, children, noBorder }) => {
    return (React.createElement(GroupWrapper, { orientation: orientation ?? Orientation.Horizontal, noBorder: noBorder }, children));
}, OptionGroupComponents);
export { OptionGroup };
//# sourceMappingURL=OptionGroup.js.map